import React, { useState, useEffect, useRef } from "react";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  applyActionCode,
} from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../firebaseConfig";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Row, Col } from "antd";
import {
  Spin,
  Card,
  Form,
  Input,
  Button,
  message,
  Typography,
  Divider,
} from "antd";
import { UserOutlined, LockOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import OAuth from "./OAuth";

export default function AdminLogin() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode");
  const apiKey = queryParams.get("apiKey");
  const actionCode = queryParams.get("oobCode");

  useEffect(() => {
    if (mode === "verifyEmail" && apiKey === process.env.REACT_APP_API_FIREBASE_KEY) {
      applyActionCode(auth, actionCode)
        .then((resp) => {
          message.success("Your email has been verified. Please login");
        })
        .catch((error) => {
          if (error === "auth/invalid-action-code") {
            message.error("Email link to verify is invalid.");
          } else {
            message.error("Unknown error verifying email");
          }
        });
    } else {
        return;
    }
  }, [actionCode, apiKey, mode]);

  const [user] = useAuthState(auth);
  let navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);

  const { Title, Text } = Typography;

  useEffect(() => {
    if (isMounted) {
      onAuthStateChanged(auth, (user) => {
        if (user) {
          navigate("/");
        }
      });
    }

    return () => {
      isMounted.current = false;
    };
  }, [navigate, isMounted]);

  const handleLogin = async () => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      message.success("Welcome back!");
      navigate("/");
    } catch (error) {
      console.log(error);
      message.error("Invalid email and/or password");
    } finally {
      setLoading(false);
    }
  };

  if (user) {
    return <Spin size="large" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }} />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      position: 'relative'
    }}>
      {/* Background decorations */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '10%',
        width: '200px',
        height: '200px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '50%',
        animation: 'float 6s ease-in-out infinite'
      }}></div>
      
      <div style={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: '150px',
        height: '150px',
        background: 'rgba(255, 215, 0, 0.2)',
        borderRadius: '50%',
        animation: 'float 4s ease-in-out infinite reverse'
      }}></div>

      {/* Back to Home Button */}
      <Link 
        to="/" 
        style={{
          position: 'absolute',
          top: '30px',
          left: '30px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: 'white',
          textDecoration: 'none',
          padding: '10px 16px',
          borderRadius: '25px',
          background: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
        }}
      >
        <ArrowLeftOutlined />
        <span>Back to Home</span>
      </Link>

      <Row justify="center" align="middle" style={{ width: '100%', maxWidth: '1200px' }}>
        <Col xs={24} sm={20} md={16} lg={12} xl={10}>
          <Card
            style={{
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              border: 'none',
              background: 'white'
            }}
            bodyStyle={{ padding: '50px 40px' }}
          >
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <Title 
                level={2} 
                style={{ 
                  color: '#1f2937', 
                  marginBottom: '8px',
                  fontSize: '2rem',
                  fontWeight: 'bold'
                }}
              >
                Welcome Back
              </Title>
              <Text style={{ 
                color: '#6b7280', 
                fontSize: '16px' 
              }}>
                Sign in to your OkBrahmin account
              </Text>
            </div>

            {/* OAuth Component */}
            <div style={{ marginBottom: '30px' }}>
              <OAuth />
            </div>

            {/* Divider */}
            <Divider style={{ margin: '30px 0' }}>
              <span style={{ color: '#9ca3af', fontSize: '14px' }}>Or continue with email</span>
            </Divider>

            {/* Login Form */}
            <Form
              layout="vertical"
              onFinish={handleLogin}
              autoComplete="off"
              requiredMark={false}
            >
              <Form.Item
                name="email"
                label={<span style={{ fontWeight: '600', color: '#374151' }}>Email Address</span>}
                rules={[
                  {
                    required: true,
                    message: "Please enter your email address",
                  },
                  {
                    type: 'email',
                    message: "Please enter a valid email address",
                  }
                ]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
                  placeholder="Enter your email"
                  size="large"
                  style={{
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px'
                  }}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={<span style={{ fontWeight: '600', color: '#374151' }}>Password</span>}
                rules={[
                  {
                    required: true,
                    message: "Please enter your password",
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                  placeholder="Enter your password"
                  size="large"
                  style={{
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px'
                  }}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Form.Item>

              {/* Forgot Password Link */}
              <div style={{ textAlign: 'right', marginBottom: '30px' }}>
                <Link 
                  to="/forgot-password"
                  style={{
                    color: '#667eea',
                    fontSize: '14px',
                    fontWeight: '500',
                    textDecoration: 'none'
                  }}
                >
                  Forgot your password?
                </Link>
              </div>

              {/* Login Button */}
              <Form.Item style={{ marginBottom: '30px' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={loading}
                  style={{
                    width: '100%',
                    height: '50px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    border: 'none',
                    fontSize: '16px',
                    fontWeight: '600',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                  }}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </Form.Item>
            </Form>

            {/* Sign Up Link */}
            <div style={{ 
              textAlign: 'center', 
              paddingTop: '20px', 
              borderTop: '1px solid #e5e7eb' 
            }}>
              <Text style={{ color: '#6b7280' }}>
                Don't have an account?{' '}
                <Link 
                  to="/register"
                  style={{
                    color: '#667eea',
                    fontWeight: '600',
                    textDecoration: 'none'
                  }}
                >
                  Create one here
                </Link>
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </div>
  );
}